import { ProjectProvider } from "./context/ProjectContext";
import React, { useEffect, useState, Component } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { Sun, Moon, ArrowRight, ArrowLeft, Download, Bell, FolderKanban } from 'lucide-react';

import { SovereignProvider } from './context/SovereignContext';
import { LandingPage } from './modules/Landing/LandingPage';
import { CivilToolkit } from './modules/CivilEngine/CivilToolkit';
import VisionLab from './modules/VisionLab/VisionLab';
import { VaultEngine } from './modules/Vault/VaultEngine';
import Dashboard from './modules/Dashboard/Dashboard';
import { CivilIntelligenceEngine } from './modules/CivilIntelligenceEngine/CivilIntelligenceEngine';
import { ProjectWorkspace } from './modules/Projects/ProjectWorkspace';

class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  render() {
    if (this.state.hasError) {
      return (
        <div className="p-6 text-white min-h-[50vh] flex flex-col items-center justify-center">
          <div className="glass-panel p-6 rounded-2xl w-full max-w-lg text-center">
            <h2 className="text-rose-400 font-bold mb-4 text-lg">خطای موقت در این بخش</h2>
            <p className="text-sm text-slate-300 mb-6 bg-slate-900/50 p-4 rounded-xl font-mono text-left overflow-auto direction-ltr">
              {this.state.error?.toString()}
            </p>
            <button onClick={() => window.location.href = '/'} className="px-6 py-2 bg-sky-500 text-white rounded-xl font-bold hover:bg-sky-600 transition">
              بازگشت به صفحه اصلی
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const LandingContainer = () => {
  const navigate = useNavigate();
  return <LandingPage onNavigate={(path) => navigate(path)} />;
};

const ToolWrapper = ({ children, title }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [notifyGranted, setNotifyGranted] = useState(typeof Notification !== 'undefined' ? Notification.permission === 'granted' : false);

  useEffect(() => {
    if (typeof document !== 'undefined' && document.body && document.body.classList && localStorage.getItem('nexus_theme') === 'light') {
// safely handled in context
    }
  }, [location.pathname]);

  const handlePushClick = async () => {
    if ('Notification' in window) {
      const perm = await Notification.requestPermission();
      if (perm === 'granted') setNotifyGranted(true);
    }
  };

  const toggleTheme = () => {
    if (typeof document !== 'undefined' && document.body && document.body.classList) {
// safely handled in context
    }
    if (typeof document !== 'undefined' && document.body && document.body.classList) {
      localStorage.setItem('nexus_theme', document.body.classList.contains('light-theme') ? 'light' : 'dark');
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-transparent font-sans">
      <header className="sticky top-2 z-40 glass-panel mx-3 sm:mx-6 mt-3 px-4 py-3 rounded-2xl flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button onClick={() => navigate('/')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white/10 hover:bg-white/20 text-sky-400 [body.light-theme_&]:bg-slate-100 [body.light-theme_&]:text-sky-600 transition shadow-sm border border-white/5">
            <ArrowRight className="w-3.5 h-3.5" />
            <span className="text-xs font-bold">صفحه اصلی</span>
          </button>

          {location.pathname !== '/projects' && (
            <button onClick={() => navigate('/projects')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 border border-amber-500/20 transition text-xs font-bold">
              <FolderKanban className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">پروژه‌ها</span>
            </button>
          )}
        </div>

        <span className="text-xs sm:text-sm font-black tracking-wider text-transparent bg-clip-text bg-gradient-to-r from-sky-400 to-indigo-400">
          {title}
        </span>

        <div className="flex items-center gap-2">
          {!notifyGranted && (
            <button onClick={handlePushClick} className="p-2 rounded-xl bg-white/5 border border-white/10 text-amber-400 [body.light-theme_&]:bg-slate-100 [body.light-theme_&]:text-amber-600 transition active:scale-95">
              <Bell className="w-4 h-4 attention-wiggle" />
            </button>
          )}
          <button onClick={toggleTheme} className="p-2 rounded-xl bg-white/5 border border-white/10 text-slate-300 [body.light-theme_&]:bg-slate-100 [body.light-theme_&]:text-slate-600 transition active:scale-95">
            <Sun className="w-4 h-4 hidden [body.light-theme_&]:block text-amber-500" />
            <Moon className="w-4 h-4 block [body.light-theme_&]:hidden" />
          </button>
        </div>
      </header>

      <main className="flex-1 max-w-5xl w-full mx-auto p-4 sm:p-6 glass-panel rounded-3xl mt-8 mb-10 shadow-2xl">
        <ErrorBoundary>
          {children}
        </ErrorBoundary>
      </main>
    </div>
  );
};

const App = () => {
  return (
    <Router>
      <SovereignProvider>
        <ProjectProvider>
        <Routes>
          <Route path="/" element={<LandingContainer />} />
          <Route path="/projects" element={<ToolWrapper title="PROJECT WORKSPACE"><ProjectWorkspace /></ToolWrapper>} />
          <Route path="/tools/engineering" element={<ToolWrapper title="جعبه‌ابزار مهندسی"><CivilToolkit /></ToolWrapper>} />
          <Route path="/tools/document" element={<ToolWrapper title="دستیار هوشمند اسناد"><CivilIntelligenceEngine /></ToolWrapper>} />
          <Route path="/tools/image" element={<ToolWrapper title="تشخیص ترک و پایش سازه"><VisionLab /></ToolWrapper>} />
          <Route path="/tools/vault" element={<ToolWrapper title="گاوصندوق اسناد امن"><VaultEngine /></ToolWrapper>} />
          <Route path="/tools/productivity" element={<ToolWrapper title="میزکار تمرکز و کارگاه"><Dashboard /></ToolWrapper>} />
          <Route path="*" element={<LandingContainer />} />
        </Routes>
      </ProjectProvider>
      </SovereignProvider>
    </Router>
  );
};

export default App;
